import { Component, Input, forwardRef, signal, computed, inject, OnInit, OnDestroy, ViewChild, TemplateRef, ElementRef, ViewContainerRef, Injector, runInInjectionContext, } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, FormGroup, } from '@angular/forms';
import { LocalizationService } from '@abp/ng.core';
import { of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Overlay, OverlayRef, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { localizeWithFallback } from '../../utils/localization-utils';

import { FieldDefinition } from '../../fields/field-definition';
import { PreAccessFilterValue, PERIOD_OPTIONS, PreAccessFieldType } from '../models/pre-access-filter.models';
import { calculatePeriod } from '../../generic-table/filters/utils/period-calculator';
import { ZnMultiSelect } from '../../zn-select/zn-multi-select';
import { ZnSingleSelect } from '../../zn-select/zn-single-select';
import { ControlTypes } from '../../generic-form/enums/control-types';
import { InputTypes } from '../../generic-form/enums/input-type';
import { formatRangeDisplayValue } from '../../generic-table/filters/utils/range-display';
import { buildRangeSearchTerms } from '../../generic-table/filters/utils/range-terms';
import {
  formatDateDisplayValue,
  formatInputToIso,
  formatIsoToInput,
} from '../../generic-table/filters/utils/date-value';
import { getRangeModeOptions } from '../../generic-table/filters/utils/range-modes';
import { getEqualLabelKeyForType } from '../../generic-table/filters/utils/range-labels';

/**
 * Internal value structure for the filter field
 */
interface FilterFieldValue {
  mode: 'equal' | 'range' | 'periods';
  equalValue?: any;
  fromValue?: any;
  toValue?: any;
  periodValue?: string;
  selectedOptions?: string[];
  checkboxValue?: boolean | null;
}

/**
 * Mode option for the dropdown selector
 */
@Component({
  selector: 'app-pre-access-filter-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ZnMultiSelect, ZnSingleSelect, OverlayModule],
  templateUrl: './pre-access-filter-field.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PreAccessFilterField),
      multi: true,
    },
  ],
})
export class PreAccessFilterField implements ControlValueAccessor, OnInit, OnDestroy {
  private static inputSequence = 0;
  readonly equalInputId = `pre-access-filter-equal-${PreAccessFilterField.inputSequence++}`;

  private readonly localizationService = inject(LocalizationService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);

  @Input() field!: FieldDefinition;
  @Input() parentForm?: FormGroup;

  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;
  @ViewChild('inputTrigger', { read: ElementRef }) inputTrigger!: ElementRef;

  isDropdownOpen = signal(false);
  private overlayRef: OverlayRef | null = null;

  currentMode = signal<'equal' | 'range' | 'periods'>('equal');
  equalValue = signal<any>(null);
  fromValue = signal<any>(null);
  toValue = signal<any>(null);
  periodValue = signal<string>('');
  selectedOptions = signal<string[]>([]);
  checkboxValue = signal<boolean | null>(null);

  private onChange: (value: PreAccessFilterValue | null) => void = () => {};
  private onTouched: () => void = () => {};
  private subscription?: Subscription;
  private dependencySubscriptions: Subscription[] = [];
  private dependentValues = signal<Record<string, any>>({});

  fieldType = computed<PreAccessFieldType>(() => this.detectFieldType());
  fieldLabel = computed(() => localizeWithFallback(this.localizationService, this.field?.label || ''));

  supportsModes = computed(() => {
    const type = this.fieldType();
    return type === 'date' || type === 'datetime' || type === 'number' || type === 'time';
  });

  supportsPeriods = computed(() => {
    const type = this.fieldType();
    return type === 'date' || type === 'datetime';
  });

  periodOptions = PERIOD_OPTIONS;

  modeOptions = computed(() => getRangeModeOptions(this.supportsPeriods()));
  equalLabelKey = computed(() => {
    const type = this.fieldType();
    if (type === 'time') return '';
    if (type === 'number') return getEqualLabelKeyForType('number');
    if (type === 'date') return getEqualLabelKeyForType('date');
    if (type === 'datetime') return getEqualLabelKeyForType('datetime');
    return '';
  });
  showRangeLabels = computed(() => this.fieldType() !== 'time');

  displayValue = computed(() => this.formatDisplayValue());
  displayTriggerValue = computed(() => this.displayValue() || this.prefilterEmptyDisplayValue());

  dropdownOptions = computed(() => {
    const options = this.field?.fieldOptions?.options;
    if (!options) return [];
    return options.map(o => o.value);
  });

  prefilterOptionsLoader = computed(() => {
    const field = this.field;
    if (!field) return undefined;

    const prefilter = field.fieldOptions?.prefilter;
    const fkLoader = field.foreignKey?.loader;
    const optionsLoader = field.fieldOptions?.optionsLoader;

    const runOptionsLoader = (params?: any) =>
      runInInjectionContext(this.injector, () => optionsLoader?.(params));

    const runFkLoader = (params?: any, queryOverride?: any) =>
      runInInjectionContext(this.injector, () =>
        fkLoader?.(params?.forceRefresh, queryOverride)
      );

    const normalizeQueryParams = (query: Record<string, any>) =>
      Object.fromEntries(
        Object.entries(query).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join(',') : value,
        ])
      );

    const displayField = field.foreignKey?.displayField ?? 'name';
    const keyField = field.foreignKey?.keyField ?? 'id';

    const buildQueryOverride = (params?: any) => {
      if (!params) return {};
      const queryOverride: Record<string, any> = {};
      if (params.search) {
        queryOverride[displayField] = params.search;
      }
      if (!queryOverride.sorting) {
        queryOverride.sorting = displayField;
      }
      if (params.ids?.length) {
        queryOverride[keyField] = params.ids;
      }
      if (params.skipCount !== undefined) {
        queryOverride.skipCount = params.skipCount;
      }
      if (params.maxResultCount !== undefined) {
        queryOverride.maxResultCount = params.maxResultCount;
      }
      return queryOverride;
    };

    if (!fkLoader) {
      return optionsLoader ? (params?: any) => runOptionsLoader(params) : undefined;
    }

    const toOptions = (items: any[]) => {
      const resolvePath = (obj: any, path: string) =>
        path.split('.').reduce((cur, part) => cur?.[part], obj);

      return items.map(item => {
        const displayValue = displayField.includes('.')
          ? resolvePath(item, displayField)
          : item?.[displayField];
        return {
          key: String(item?.[keyField] ?? ''),
          value: String(displayValue ?? ''),
        };
      });
    };

    const dependsOn = prefilter?.dependsOn;
    const hasDependencies = Array.isArray(dependsOn)
      ? dependsOn.length > 0
      : dependsOn && Object.keys(dependsOn).length > 0;

    if (!hasDependencies || !prefilter?.buildQuery) {
      if (hasDependencies && !prefilter?.buildQuery) {
        const deps = Array.isArray(dependsOn) ? dependsOn : Object.keys(dependsOn as Record<string, string>);
        const values = this.dependentValues();
        const hasMissing = deps.some(dep => values[dep] === null || values[dep] === undefined || values[dep] === '');
        const query = Array.isArray(dependsOn)
          ? deps.reduce((acc, dep) => ({ ...acc, [dep]: values[dep] }), {})
          : Object.entries(dependsOn as Record<string, string>).reduce(
              (acc, [dep, param]) => ({ ...acc, [param]: values[dep] }),
              {}
            );
        const normalizedQuery = normalizeQueryParams(query);

        return (params?: any) => {
          const mergedQuery = normalizeQueryParams({
            ...normalizedQuery,
            ...buildQueryOverride(params),
          });
          if (hasMissing) {
            return runFkLoader(params, mergedQuery)?.pipe(map(items => toOptions(items)));
          }
          return runFkLoader(params, mergedQuery)?.pipe(map(items => toOptions(items)));
        };
      }
      return optionsLoader
        ? (params?: any) => runOptionsLoader(params)
        : (params?: any) => {
            const mergedQuery = normalizeQueryParams(buildQueryOverride(params));
            return runFkLoader(params, mergedQuery)?.pipe(map(items => toOptions(items)));
          };
    }

    const deps = Array.isArray(dependsOn) ? dependsOn : Object.keys(dependsOn as Record<string, string>);
    const values = this.dependentValues();
    const hasMissing = deps.some(dep => values[dep] === null || values[dep] === undefined || values[dep] === '');
    const query = normalizeQueryParams(prefilter.buildQuery(values));

    return (params?: any) => {
      const mergedQuery = normalizeQueryParams({
        ...query,
        ...buildQueryOverride(params),
      });
      if (hasMissing) {
        return runFkLoader(params, mergedQuery)?.pipe(map(items => toOptions(items)));
      }
      return runFkLoader(params, mergedQuery)?.pipe(map(items => toOptions(items)));
    };
  });

  prefilterShowPlaceholder = computed(
    () => this.field?.fieldOptions?.prefilter?.showPlaceholder ?? true
  );
  prefilterPlaceholderKey = computed(
    () => this.field?.fieldOptions?.prefilter?.placeholderKey ?? ''
  );
  prefilterEmptyDisplayValueKey = computed(
    () => this.field?.fieldOptions?.prefilter?.emptyDisplayValueKey ?? ''
  );
  prefilterEmptyDisplayValue = computed(() => {
    const key = this.prefilterEmptyDisplayValueKey();
    if (!key) {
      return '';
    }
    const resolvedKey = key.includes('::') ? key : `LanguageService::${key}`;
    return this.translate(resolvedKey);
  });

  ngOnInit(): void {
    const defaultMode = this.field?.fieldOptions?.prefilter?.defaultMode;
    if (defaultMode) {
      this.currentMode.set(defaultMode);
    }

    const defaultValue = this.field?.fieldOptions?.prefilter?.defaultValue;
    if (defaultValue !== undefined) {
      this.setDefaultValue(defaultValue);
    }

    this.setupDependencies();
    this.applyAutoSelectFirstIfNeeded();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.dependencySubscriptions.forEach(sub => sub.unsubscribe());
    this.closeDropdown();
  }

  private setDefaultValue(value: any): void {
    const type = this.fieldType();

    if (type === 'checkbox') {
      this.checkboxValue.set(value);
    } else if (type === 'foreignKey' || type === 'dropdown') {
      this.selectedOptions.set(Array.isArray(value) ? value : [value]);
    } else {
      this.equalValue.set(value);
    }

    this.emitChange();
  }

  private detectFieldType(): PreAccessFieldType {
    const fieldOptions = this.field?.fieldOptions;
    const controlType = fieldOptions?.controlType;
    const type = fieldOptions?.type;

    if (controlType === ControlTypes.Checkbox || type === InputTypes.Checkbox) {
      return 'checkbox';
    }

    if (controlType === ControlTypes.Dropdown) {
      if (type === 'foreignKey' || fieldOptions?.optionsLoader) {
        return 'foreignKey';
      }
      return 'dropdown';
    }

    if (controlType === ControlTypes.Date || type === 'date') {
      return 'date';
    }

    if (controlType === ControlTypes.Time || type === InputTypes.Time) {
      return 'time';
    }

    if (type === InputTypes.DatetimeLocal) {
      return 'datetime';
    }

    if (type === InputTypes.Number) {
      return 'number';
    }

    return 'text';
  }

  private setupDependencies(): void {
    const prefilter = this.field?.fieldOptions?.prefilter;
    const dependsOn = prefilter?.dependsOn;
    if (!this.parentForm || !dependsOn) return;

    const deps = Array.isArray(dependsOn) ? dependsOn : Object.keys(dependsOn);
    if (deps.length === 0) return;

    const updateValues = () => {
      const values: Record<string, any> = {};
      deps.forEach(dep => {
        const control = this.parentForm?.get(dep);
        values[dep] = this.extractDependentValue(control?.value);
      });
      this.dependentValues.set(values);
    };

    deps.forEach(dep => {
      const control = this.parentForm?.get(dep);
      if (!control) return;

      this.dependencySubscriptions.push(
        control.valueChanges.subscribe(() => {
          updateValues();
          if (this.fieldType() === 'foreignKey') {
            this.selectedOptions.set([]);
            this.emitChange();
          }
        })
      );
    });

    updateValues();
  }

  private extractDependentValue(value: PreAccessFilterValue | null): any {
    if (!value || !Array.isArray(value.searchTerms)) {
      return null;
    }
    if (value.searchTerms.length === 0) {
      return null;
    }
    return value.searchTerms.length === 1 ? value.searchTerms[0] : value.searchTerms;
  }

  toggleDropdown(): void {
    if (this.isDropdownOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    if (this.isDropdownOpen()) return;

    this.isDropdownOpen.set(true);

    const config = this.getOverlayConfig();
    this.overlayRef = this.overlay.create(config);

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());

    this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.closeDropdown();
      }
    });
  }

  closeDropdown(): void {
    if (!this.isDropdownOpen()) return;

    this.isDropdownOpen.set(false);
    this.onTouched();

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private getOverlayConfig(): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.inputTrigger)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4,
        },
      ])
      .withPush(false);

    return new OverlayConfig({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'pre-access-filter-dropdown-pane',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.inputTrigger?.nativeElement?.offsetWidth || 280,
      minWidth: 280,
    });
  }

  onApply(): void {
    this.emitChange();
    this.closeDropdown();
  }

  private formatDisplayValue(): string {
    const type = this.fieldType();

    if (!this.supportsModes()) {
      return '';
    }

    const mode = this.currentMode();

    if (mode === 'equal' || mode === 'range') {
      return formatRangeDisplayValue(
        mode,
        {
          equal: this.equalValue(),
          from: this.fromValue(),
          to: this.toValue(),
        },
        value => this.formatValueForDisplay(value)
      );
    }

    if (mode === 'periods') {
      const period = this.periodValue();
      if (!period) return '';

      const periodOption = this.periodOptions.find(p => p.value === period);
      return periodOption ? this.translate(periodOption.labelKey) : '';
    }

    return '';
  }

  private formatValueForDisplay(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const type = this.fieldType();

    if (type === 'date' || type === 'datetime') {
      return formatDateDisplayValue(value);
    }

    if (type === 'time') {
      return String(value);
    }

    if (type === 'number') {
      return String(value);
    }

    return String(value);
  }

  writeValue(value: PreAccessFilterValue | null): void {
    if (!value) {
      this.resetValues();
      return;
    }

    if (value.mode) {
      this.currentMode.set(value.mode as 'equal' | 'range' | 'periods');
    }

    const type = this.fieldType();
    const terms = value.searchTerms || [];

    if (type === 'checkbox') {
      this.checkboxValue.set(terms[0] ?? null);
    } else if (type === 'foreignKey' || type === 'dropdown') {
      this.selectedOptions.set(terms.map(String));
    } else if (this.supportsModes()) {
      if (terms.length === 1) {
        this.equalValue.set(this.parseValueForInput(terms[0]));
      } else if (terms.length === 2) {
        this.fromValue.set(this.parseValueForInput(terms[0]));
        this.toValue.set(this.parseValueForInput(terms[1]));
      }
    } else {
      this.equalValue.set(terms[0] ?? null);
    }
  }

  registerOnChange(fn: (value: PreAccessFilterValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setMode(mode: 'equal' | 'range' | 'periods'): void {
    this.currentMode.set(mode);
  }

  onEqualValueChange(value: any): void {
    this.equalValue.set(value);
  }

  onFromValueChange(value: any): void {
    this.fromValue.set(value);
  }

  onToValueChange(value: any): void {
    this.toValue.set(value);
  }

  onPeriodChange(period: string): void {
    this.periodValue.set(period);
  }

  onOptionsChange(values: string | string[] | null): void {
    if (values === null) {
      this.selectedOptions.set([]);
    } else if (Array.isArray(values)) {
      this.selectedOptions.set(values);
    } else {
      this.selectedOptions.set([values]);
    }
    this.emitChange();
  }

  onCheckboxChange(value: boolean | null): void {
    this.checkboxValue.set(value);
    this.emitChange();
  }

  private emitChange(): void {
    this.onTouched();
    const filterValue = this.buildFilterValue();
    this.onChange(filterValue);
  }

  private buildFilterValue(): PreAccessFilterValue | null {
    const type = this.fieldType();
    const columnId = this.field?.key || '';
    const queryKey = this.field?.fieldOptions?.prefilter?.queryKey;

    if (type === 'checkbox') {
      const val = this.checkboxValue();
      if (val === null) {
        return null;
      }
      return {
        columnId,
        queryKey,
        searchTerms: [val],
        operator: 'EQ',
      };
    }

    if (type === 'foreignKey' || type === 'dropdown') {
      const selected = this.selectedOptions();
      if (selected.length === 0) {
        return null;
      }
      return {
        columnId,
        queryKey,
        searchTerms: selected,
        operator: 'IN',
      };
    }

    if (type === 'text') {
      const val = this.equalValue();
      if (!val || val.trim() === '') {
        return null;
      }
      return {
        columnId,
        queryKey,
        searchTerms: [val],
        operator: 'Contains',
      };
    }

    const mode = this.currentMode();

    const terms = buildRangeSearchTerms(
      mode,
      {
        equal: this.equalValue(),
        from: this.fromValue(),
        to: this.toValue(),
        period: this.periodValue(),
      },
      {
        formatValue: value => this.formatValueForOutput(value),
        periodToRange: period => calculatePeriod(period, type === 'datetime'),
      }
    );

    if (terms.length === 0) {
      return null;
    }

    return {
      columnId,
      queryKey,
      searchTerms: terms,
      operator: mode === 'equal' ? 'EQ' : 'RangeInclusive',
      mode,
    };

    return null;
  }

  private formatValueForOutput(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const type = this.fieldType();

    if (type === 'date' || type === 'datetime') {
      return formatInputToIso(value, type === 'datetime');
    }

    if (type === 'time') {
      return value;
    }

    if (type === 'number') {
      return Number(value);
    }

    return value;
  }

  private parseValueForInput(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    const type = this.fieldType();

    if (type === 'date' || type === 'datetime') {
      return formatIsoToInput(value, type === 'datetime');
    }

    return value;
  }

  private resetValues(): void {
    this.equalValue.set(null);
    this.fromValue.set(null);
    this.toValue.set(null);
    this.periodValue.set('');
    this.selectedOptions.set([]);
    this.checkboxValue.set(null);
  }

  translate(key: string): string {
    return localizeWithFallback(this.localizationService, key);
  }

  getInputType(): string {
    const type = this.fieldType();
    switch (type) {
      case 'date':
        return 'date';
      case 'datetime':
        return 'datetime-local';
      case 'time':
        return 'time';
      case 'number':
        return 'number';
      default:
        return 'text';
    }
  }

    private applyAutoSelectFirstIfNeeded(): void {
    const prefilter = this.field?.fieldOptions?.prefilter;
    if (!prefilter?.autoSelectFirst) {
      return;
    }

    if (this.selectedOptions().length > 0) {
      return;
    }

    const loader = this.prefilterOptionsLoader();
    if (!loader) {
      return;
    }

    const stream$ = loader();
    if (!stream$ || typeof stream$.pipe !== 'function') {
      return;
    }

    const subscription = stream$.pipe(take(1)).subscribe({
      next: response => {
        const items = Array.isArray(response) ? response : response?.items ?? [];
        if (items.length === 0) {
          return;
        }
        const firstKey = String(items[0].key);
        if (this.selectedOptions().length > 0) {
          return;
        }
        this.selectedOptions.set([firstKey]);
        this.emitChange();
      },
    });

    this.dependencySubscriptions.push(subscription);
  }
}
